import { useCurrency } from "@/hooks/useCurrency";
import { useGeo } from "@/i18n/routing";
import { useFlag } from "@/lib/flags/client";
import { useToken, useUser } from "@frend-digital/centra/client";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { rpc } from "#rpc";
import { type Currency, convertPointsTo } from "./conversion-rate";
import { calculatePointsProgress } from "./utils";

export const useLoyaltyProfile = () => {
	const { data: currency } = useCurrency();
	const { geo } = useGeo();
	const loyaltyEnabled = useFlag("loyalty.enabled");
	return useQuery({
		queryKey: ["loyalty", "profile", geo],
		queryFn: async () => {
			return await rpc.loyalty.getLoyaltyProfile(geo);
		},
		enabled: loyaltyEnabled,
		select: (data) => {
			const points = data.points_balance || 0;
			const converted = convertPointsTo((currency as Currency) || "NOK", points);
			const formatter = new Intl.NumberFormat(geo, {
				style: "currency",
				currencySign: "standard",
				currency: (currency as Currency) || "NOK",
			});

			const pointsClamped = Math.floor(points / 1000) * 1000;
			const valueClamped = convertPointsTo((currency as Currency) || "NOK", pointsClamped);

			return {
				...data,

				points: data.points_balance || 0,
				currency,
				/**
				 * The total value of points in customer currency as number
				 */
				valueAsNumber: converted,
				/**
				 * The total value of points in customer currency
				 */
				value: formatter.format(converted),
				pointsProgress: calculatePointsProgress(points),
				/**
				 * Value as clamped points
				 */
				valueClamped: formatter.format(valueClamped),

				/**
				 * Points clamped to nearest 1000
				 */
				pointsClamped,
			};
		},
	});
};

export const useLoyaltyTokenQuery = () => {
	const token = useToken();
	const { data: user } = useUser();
	const loyaltyEnabled = useFlag("loyalty.enabled");
	const { geo } = useGeo();

	return useQuery({
		queryKey: ["loyalty", "sha256Token", geo],
		queryFn: async () => {
			return await rpc.loyalty.getTokenForLoggedInUser(geo).then((r) => r.token);
		},
		enabled: loyaltyEnabled && !!user && !!token,
	});
};

export const useLoyaltyToken = () => {
	const { data: sha256Token } = useLoyaltyTokenQuery();
	return sha256Token;
};

export const campaignsQueryOptions = (geo: string) =>
	queryOptions({
		queryKey: ["loyalty", "campaigns", geo],
		queryFn: async () => {
			return (await rpc.loyalty.getCampaigns(geo)) || [];
		},
	});

export const useLoyaltyCampaigns = () => {
	const loyaltyEnabled = useFlag("loyalty.enabled");
	const { geo } = useGeo();
	return useQuery({
		...campaignsQueryOptions(geo),
		enabled: loyaltyEnabled,
	});
};

export type Campaign = NonNullable<ReturnType<typeof useLoyaltyCampaigns>["data"]>[number];

export const useIncompleteCampaigns = () => {
	const loyaltyEnabled = useFlag("loyalty.enabled");
	const { geo } = useGeo();
	return useQuery({
		...campaignsQueryOptions(geo),
		enabled: loyaltyEnabled,
		select: (data) => {
			type Data = typeof data;
			type Campaign = NonNullable<Data>[number];

			return data?.filter((c) => {
				const cc = c as Campaign;

				return "status" in cc && (cc.status?.customer_times_completed || 0) === 0;
			});
		},
	});
};

export const useNextCampaign = () => {
	const { data: incompleteCampaigns, error } = useIncompleteCampaigns();

	const complete_account = incompleteCampaigns?.find((c) => c.action_name === "complete_profile");

	const isLoading = !incompleteCampaigns && !error;

	return {
		data: complete_account,
		isLoading,
	};
};

export const redemptionOptionsQuery = (geo: string) =>
	queryOptions({
		queryKey: ["loyalty", "redemptionOptions", geo],
		queryFn: async () => {
			return (await rpc.loyalty.getRedemptionOptions(geo)) || [];
		},
	});

export const useRedemptionOptions = () => {
	const loyaltyEnabled = useFlag("loyalty.enabled");
	const { geo } = useGeo();
	return useQuery({
		...redemptionOptionsQuery(geo),
		enabled: loyaltyEnabled,
	});
};

export type RedemptionOption = NonNullable<ReturnType<typeof useRedemptionOptions>["data"]>[number];

export const useRedeemVariablePoints = () => {
	const { geo } = useGeo();
	return useMutation({
		mutationFn: async ({ points, optionId }: { points: number; optionId: number }) => {
			return await rpc.loyalty.redeem.variable({ points, optionId, locale: geo });
		},
	});
};

export const useRedeemFixedPoints = () => {
	const { geo } = useGeo();
	return useMutation({
		mutationFn: async ({ optionId }: { optionId: number }) => {
			return await rpc.loyalty.redeem.fixed({ optionId, locale: geo });
		},
	});
};

export const useRefreshLoyalty = () => {
	const queryClient = useQueryClient();
	const refresh = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["loyalty"] });
	}, [queryClient]);

	return refresh;
};

export const useEarningRules = () => {
	const { geo } = useGeo();
	return useQuery({
		queryKey: ["loyalty", "earningRules", geo],
		queryFn: async () => {
			return await rpc.loyalty.getEarningRules(geo);
		},
		staleTime: 60 * 60 * 1000, // 1 hour — mirrors server cache
	});
};
